import React, { FC, ReactNode, SyntheticEvent, useEffect, useReducer, useState } from 'react';

import styled from '@emotion/styled';
import { FeatureFlag } from 'generated/drupal-graphql';
import { isBrowserIE, useSiteMetadata, setMetatagCookies, getI18nData, languages } from '@lucid/utils';
import { graphql, useStaticQuery } from 'gatsby';
import {
  ABTestAdminUi,
  AlertBanner,
  AnnouncementBanner,
  Box,
  CMSNodeButton,
  FontLoader,
  LayoutContext,
  Modal,
  Pingdom,
  SEO,
  SiteFooterOptionalProps,
  SiteFooterSocialLinks,
  SiteFooter,
  ThemeProvider,
  GlobalCSS,
} from '@lucid/components-react';
import { theme } from '@lucid/theme';

import Navbar, { NavbarOptionalProps } from 'components/navbar/navbar';
import { useMenuFooter } from 'hooks/use-menu';
import { ExtendedDrupalNode, PageMetatag, I18n } from 'types/types';

interface ABTest {
  [key: string]: string;
}

interface ABTestObj {
  [key: string]: ABTest;
}

interface StaticQuery {
  allFeatureFlag: {
    nodes: FeatureFlag[];
  };
  allFooterSocialIconsJson: {
    edges: SiteFooterSocialLinks[];
  };
  i18nData: I18n.DataEdges;
}

interface LayoutProps {
  langPrefix: string;
  entityTranslations?: ExtendedDrupalNode[];
  metatags?: PageMetatag[] | null;
  schema?: string | null;
  location: {
    pathname: string;
    origin: string;
  };
  pageType: string;
  children: ReactNode;
  nid?: string;
  shouldHideSsr: boolean;
  navbarProps?: NavbarOptionalProps;
  footerProps?: SiteFooterOptionalProps;
  hasNavbar?: boolean | null;
  hasFooter?: boolean | null;
  abTestTemplateSectionLink?: string | null | undefined;
}

const EmotionLayoutContextProvider = styled(LayoutContext.Provider)({});

const Layout: FC<LayoutProps> = ({
  langPrefix,
  location,
  pageType,
  children,
  metatags = [],
  schema = '',
  entityTranslations = [],
  nid = '',
  navbarProps,
  footerProps,
  hasNavbar = true,
  hasFooter = true,
}) => {
  const { allFeatureFlag, allFooterSocialIconsJson, i18nData } = useStaticQuery<StaticQuery>(
    graphql`
      {
        #get the announcement banner feature flag data to pass to the banner component
        allFeatureFlag(filter: { name: { eq: "Y22Q1_AnnouncementBanner" } }) {
          nodes {
            id
            name
            isActive
            variations {
              T_A
              T_B
            }
          }
        }
        allFooterSocialIconsJson {
          edges {
            node {
              name
              logo
              url
              label
            }
          }
        }
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_nav_menu
                mktg_footer_about_privacy
                mktg_footer_legal
                mktg_footer_cookie_preferences
                mktg_footer_language_switch
                mktg_footer_app_download_alt
              }
            }
          }
        }
      }
    `,
  );

  const { shortLangPrefix, i18nKey, urlLangPrefix } = languages[langPrefix as I18n.AvailableLanguagesKey];
  const { cmsUrl, product } = useSiteMetadata();
  const [modal, setModal] = useState<JSX.Element | null>(null);
  const [videoModalUrl, setVideoModalUrl] = useState('');
  const [willVideoAutoPlay, setWillVideoAutoPlay] = useState(false);
  const [videoConfig, setVideoConfig] = useState({});
  const [videoDescription, setVideoDescription] = useState('');
  const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
  const [shouldShowABTestButtons, setShouldShowABTestButtons] = useState(false);
  setMetatagCookies(metatags);

  const i18n = getI18nData(i18nData, i18nKey);

  const applicationLinks = [
    {
      url: 'https://itunes.apple.com/us/app/lucidchart-flowchart-diagram-visio-viewer/id611543423?mt=8',
      image: 'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/footer/app-store-white.svg',
      alt: i18n('mktg-footer-app-download-alt', { platform: 'Apple App Store' }) || 'Download on the Apple App Store',
      detail: 'app-store',
    },
    {
      url: 'https://play.google.com/store/apps/details?id=com.lucidchart.android.chart&amp;hl=en',
      image: 'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/footer/google-play.svg',
      alt:
        i18n('mktg-footer-app-download-alt', {
          platform: 'Google Play Store',
        }) || 'Download on the Google Play Store',
      detail: 'google-play',
    },
  ];

  const [abTests, updateTest] = useReducer((tempAbTests: ABTestObj, abTest: ABTest) => {
    const theseAbTests = { ...tempAbTests };
    const { test } = abTest;
    const currentTest = tempAbTests[test] || {};

    Object.keys(abTest).forEach(key => {
      currentTest[key] = abTest[key];
    });

    theseAbTests[test] = currentTest;

    return theseAbTests;
  }, {});

  useEffect(() => {
    setShouldShowEditButton(window.localStorage.getItem('showEditLink') === '1');
    setShouldShowABTestButtons(window.localStorage.getItem('showAbTestingUi') === '1');
  }, []);

  const openModal = (html: JSX.Element) => {
    setModal(html);
  };

  const closeModal = () => setModal(null);

  const getVideoModal = (
    thisVideoModalUrl: string,
    thisVideoDescription = '',
    autoPlay = false,
    thisVideoConfig = {},
  ) => {
    setVideoModalUrl(thisVideoModalUrl);
    setWillVideoAutoPlay(autoPlay);
    setVideoConfig(thisVideoConfig);
    setVideoDescription(thisVideoDescription);
  };

  const layoutContext = {
    language: {
      langPrefix,
      shortLangPrefix,
      urlLangPrefix,
      i18nKey,
    },
    location,
    pageType,
    product,
    video: {
      getVideoModal,
    },
    modal: {
      openModal,
    },
    abTesting: {
      updateTest,
    },
  };

  const handleCloseModal = (e: SyntheticEvent) => {
    e.preventDefault();
    setVideoModalUrl('');
  };

  // Footer props
  const footerMenu = useMenuFooter(langPrefix);
  const socialLinks = allFooterSocialIconsJson.edges as SiteFooterSocialLinks[];

  // check a few bits of data to see if the banner should render or not.
  const bannerFeatureFlag = allFeatureFlag.nodes[0] ?? undefined;
  let isBannerActive = false;
  if (bannerFeatureFlag && bannerFeatureFlag.isActive && bannerFeatureFlag.variations?.T_B === 1) {
    isBannerActive = true;
  }

  return (
    <EmotionLayoutContextProvider value={layoutContext}>
      <FontLoader />
      <GlobalCSS />
      <Pingdom />
      <ThemeProvider product={product} theme={theme}>
        {hasNavbar && (
          <Navbar
            {...navbarProps}
            i18nData={i18nData}
            langPrefix={langPrefix}
            logo={{
              imageSrc: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/lucidchart.svg',
              imageAlt: 'Go to Lucidchart homepage',
            }}
            nid={nid}
            pageType={pageType}
          />
        )}
        <Box as="main" className="gatsby-root" data-drupal-nid={nid}>
          <SEO
            entityMetatags={metatags as PageMetatag[]}
            entitySchemaMetatags={schema}
            location={location}
            pageType={pageType}
            shortLangPrefix={shortLangPrefix}
          />
          {isBannerActive && <AnnouncementBanner product={product} />}
          {children}
          <AlertBanner
            hasCondition={isBrowserIE()}
            linkProps={{
              to: 'https://lucid.co/old_browser',
              label: 'Read More',
              underline: 'always',
            }}
            storageKey="ie11-unsupported"
            variant="alert"
          >
            <strong>Lucidchart no longer supports Internet Explorer 11.</strong>
            &nbsp; Click the link to learn how to upgrade to a supported browser.
          </AlertBanner>
          {videoModalUrl && (
            <Modal
              isOpen
              onRequestClose={handleCloseModal}
              videoConfig={videoConfig}
              videoDescription={videoDescription}
              videoDescriptionId="video-modal-description"
              videoLink={videoModalUrl}
              videoShouldAutoplay={willVideoAutoPlay}
            />
          )}
          {modal && (
            <Modal isOpen onRequestClose={closeModal}>
              <Box
                css={{
                  alignSelf: 'center',
                  background: 'white',
                  borderRadius: '5px',
                  padding: '30px',
                  position: 'relative',
                  width: '100%',
                }}
              >
                {modal}
              </Box>
            </Modal>
          )}
          {shouldShowEditButton && <CMSNodeButton href={`${cmsUrl}/${urlLangPrefix}node/${nid}/edit`} />}
          {shouldShowABTestButtons && Object.keys(abTests).length > 0 && <ABTestAdminUi abTests={abTests as any} />}
        </Box>
        {hasFooter && (
          <SiteFooter
            {...footerProps}
            applicationLinks={applicationLinks}
            entityTranslations={entityTranslations}
            i18nData={i18nData}
            menu={footerMenu}
            socialLinks={socialLinks}
          />
        )}
      </ThemeProvider>
    </EmotionLayoutContextProvider>
  );
};

export default Layout;

export const query = graphql`
  fragment link on Drupal_ParagraphLink {
    fieldOpenNewTab
    fieldBeaconElementDetail
    fieldCta {
      url {
        ...getLinkPath
      }
      title
    }
  }
  fragment extraTranslations on Drupal_ParagraphExtraTranslations {
    fieldTranslationNode {
      entity {
        ...entityTranslations
      }
    }
    fieldPreferReferenceTranslati
  }
  fragment marketo on Drupal_ParagraphMarketoForm {
    entityLanguage {
      id
    }
    fieldMarketoId
    fieldMarketoTitle
    fieldMarketoBody {
      processed
    }
    fieldMarketoNote {
      processed
    }
    fieldMarketoCtaText
  }
  fragment menuFragment on Drupal_MenuLinkContent {
    menuName
    parent
    entityUuid
    entityLanguage {
      id
    }
    title
    link {
      url {
        path
      }
    }
    ... on Drupal_MenuLinkContentFooter {
      fieldBeaconElementDetail
      fieldKey
      fieldLinkOverride
    }
    ... on Drupal_MenuLinkContentIntegrationPpcNavbar {
      fieldKey
    }
    ... on Drupal_MenuLinkContentMoreExamplesList {
      fieldTranslatedLink
      fieldCategoryImage
    }
  }
`;
